import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { adminActiveSellers, getTopActiveBuyers } from '../../utils/api/admin';
import { refresh } from '../../services/auth.service';
import Spinner from '../../components/layout/Spinner';
import FadeIn from '../../utils/fadein';
import { FaEllipsisV, FaSort } from 'react-icons/fa';

function ActiveBuyers() {
  const [loaded, setLoaded] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pageBreakdown, setPageBreakdown] = useState([]);
  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState([]); // Add this new state
  const [sortField, setSortField] = useState('demo_applications');
  const [sortOrder, setSortOrder] = useState('desc');
  const [dateFilter, setDateFilter] = useState('all');
  const [lastAppFilter, setLastAppFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all'); // Add new state for source filter
  const imageUrl = process.env.REACT_APP_IMAGE_URL;
  const baseImageUrl = process.env.REACT_APP_BASE_IMAGE_URL;
  const screenshotUrl = process.env.REACT_APP_BASE_IMAGE_URL + 'companyscreenshots/';

  useEffect(() => {
    async function loadData(){
      let res = await getTopActiveBuyers(new Date().getMonth(), new Date().getFullYear());

      if (res.success){
        setData(res.data);
        setOriginalData(res.data); // Store original data
        setLoaded(true);
      }
    }

    loadData();
  }, []);

  async function loadData(month, year){
    setLoaded(false);
    let res = await getTopActiveBuyers(month, year);

    if (res.success){
      setData(res.data);
      setOriginalData(res.data); // Store original data
      setLoaded(true);
    }
  }

  async function handleMonthChange(val){
    loadData(new Date(val).getMonth(), new Date(val).getFullYear);
  }

  const sortData = (field) => {
    const sorted = [...data].sort((a, b) => {
      if (field === 'company_name') {
        return b[field].localeCompare(a[field]);
      }
      if (field === 'date_created') {
        return new Date(b.date_created) - new Date(a.date_created);
      }
      return b[field] - a[field];
    });
    setData(sorted);
    setSortField(field);
  };

  const filterByLastApp = (days) => {
    setLastAppFilter(days);
    let filteredData = originalData;
    
    // First apply registration date filter if active
    if (dateFilter !== 'all') {
      const now = new Date();
      filteredData = filteredData.filter(item => {
        const createdDate = new Date(item.date_created);
        const diffTime = Math.abs(now - createdDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= parseInt(dateFilter);
      });
    }

    // Then apply last application filter
    if (days !== 'all') {
      const now = new Date();
      filteredData = filteredData.filter(item => {
        if (!item.last_demo_application) return false;
        const lastAppDate = new Date(item.last_demo_application);
        const diffTime = Math.abs(now - lastAppDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= parseInt(days);
      });
    }

    setData(filteredData);
  };

  // Update filterByDate to also apply last application filter
  const filterByDate = (days) => {
    setDateFilter(days);
    let filteredData = originalData;
    
    if (days !== 'all') {
      const now = new Date();
      filteredData = filteredData.filter(item => {
        const createdDate = new Date(item.date_created);
        const diffTime = Math.abs(now - createdDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= parseInt(days);
      });
    }

    // Apply last application filter if active
    if (lastAppFilter !== 'all') {
      const now = new Date();
      filteredData = filteredData.filter(item => {
        if (!item.last_demo_application) return false;
        const lastAppDate = new Date(item.last_demo_application);
        const diffTime = Math.abs(now - lastAppDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= parseInt(lastAppFilter);
      });
    }

    setData(filteredData);
  };

  const filterBySource = (source) => {
    setSourceFilter(source);
    let filteredData = originalData;
    
    // Apply source filter
    if (source !== 'all') {
      filteredData = filteredData.filter(item => item.source === source);
    }

    // Apply other existing filters
    if (dateFilter !== 'all') {
      const now = new Date();
      filteredData = filteredData.filter(item => {
        const createdDate = new Date(item.date_created);
        const diffTime = Math.abs(now - createdDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= parseInt(dateFilter);
      });
    }

    if (lastAppFilter !== 'all') {
      const now = new Date();
      filteredData = filteredData.filter(item => {
        if (!item.last_demo_application) return false;
        const lastAppDate = new Date(item.last_demo_application);
        const diffTime = Math.abs(now - lastAppDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= parseInt(lastAppFilter);
      });
    }

    // Apply search filter if exists
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filteredData = filteredData.filter(item => 
        item.company_name?.toLowerCase().includes(searchLower) ||
        item.first_name?.toLowerCase().includes(searchLower) ||
        item.last_name?.toLowerCase().includes(searchLower) ||
        item.job_title?.toLowerCase().includes(searchLower)
      );
    }

    setData(filteredData);
  };

  // Add new search function
  const handleSearch = (term) => {
    setSearchTerm(term);
    let filteredData = originalData;
    
    if (term) {
      const searchLower = term.toLowerCase();
      filteredData = filteredData.filter(item => 
        item.company_name?.toLowerCase().includes(searchLower) ||
        item.first_name?.toLowerCase().includes(searchLower) ||
        item.last_name?.toLowerCase().includes(searchLower) ||
        item.job_title?.toLowerCase().includes(searchLower)
      );
    }

    // Apply existing filters
    if (dateFilter !== 'all') {
      const now = new Date();
      filteredData = filteredData.filter(item => {
        const createdDate = new Date(item.date_created);
        const diffTime = Math.abs(now - createdDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= parseInt(dateFilter);
      });
    }
    if (lastAppFilter !== 'all') {
      const now = new Date();
      filteredData = filteredData.filter(item => {
        if (!item.last_demo_application) return false;
        const lastAppDate = new Date(item.last_demo_application);
        const diffTime = Math.abs(now - lastAppDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= parseInt(lastAppFilter);
      });
    }

    setData(filteredData);
  };

  return (
    <div>
      <Helmet>
        <title>Admin - Active Buyers</title>
      </Helmet>

      <h1>Active Buyers {`(${data.length})`}</h1>


      {loaded===false && (
        <Spinner />
      )}

      {loaded && data.length == 0 && (
        <div className="center mt-20">
          No records found...
        </div>
      )}

      {loaded && (
        <div className='admindashboard__activesellers-list'>
          <div className="mb-3 flex gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search by name, company, or job title..."
                className="input w-full"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
            
            <div>
              <label className='text-sm text-gray-600'>Sort By:</label>
              <select 
                className='input select' 
                value={sortField}
                onChange={(e) => sortData(e.target.value)}
              >
                <option value="date_created">Sort by Date Created</option>
                <option value="demo_applications">Sort by Applications</option>
                <option value="demo_approved">Sort by Approved</option>
                <option value="demo_completed">Sort by Completed</option>
                <option value="demo_declined">Sort by Declined</option>
                <option value="company_name">Sort by Company Name</option>
                <option value="last_demo_application">Sort by Last Application</option>
              </select>
              </div>

          <div>
            <label className='text-sm text-gray-600'>Filter by Registration Date:</label>
            <select 
              className='input select' 
              value={dateFilter}
              onChange={(e) => filterByDate(e.target.value)}
            >
              <option value="all">All Time</option>
              <option value="30">Past 30 Days</option>
              <option value="60">Past 60 Days</option>
              <option value="90">Past 90 Days</option>
            </select>
              </div>

              <div>
              <label className='text-sm text-gray-600'>Filter by Last Application:</label>
              <select 
                className='input select' 
                value={lastAppFilter}
                onChange={(e) => filterByLastApp(e.target.value)}
              >
                <option value="all">All Time</option>
                <option value="30">Last 30 Days</option>
                <option value="60">Last 60 Days</option>
                <option value="90">Last 90 Days</option>
              </select>
            </div>

            <div>
              <label className='text-sm text-gray-600'>Filter by Source:</label>
              <select 
                className='input select' 
                value={sourceFilter}
                onChange={(e) => filterBySource(e.target.value)}
              >
                <option value="all">All Sources</option>
                {/* Get unique sources from originalData */}
                {[...new Set(originalData.map(item => item.source || 'N/A'))].map(source => (
                  <option key={source} value={source}>{source}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="table-container" style={{ 
            maxHeight: 'calc(100vh - 300px)', 
            overflow: 'auto',
            position: 'relative'
          }}>
            <table className="table" style={{ position: 'relative' }}>
              <thead style={{ 
                position: 'sticky', 
                top: 0, 
                backgroundColor: 'white', 
                zIndex: 10,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <tr>
                  <th>Logo</th>
                  <th>Company</th>
                  <th>Applications</th>
                  <th>Approved</th>
                  <th>Completed</th>
                  <th>Declined</th>
                  <th>Last Application</th>
                </tr>
              </thead>
              <tbody>
                {data.map(item => (
                  <tr 
                    key={item.company_id}
                    className={item.demo_approved == 0 ? 'bg-red-50' : ''}
                    style={item.demo_approved == 0 ? {backgroundColor: '#fee2e2'} : {}}
                  >
                    <td>
                      <img 
                        src={imageUrl + 'tr:di-@@companylogos@@default-buyer.png/' + item.logo}
                        alt={item.company_name}
                        style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                      />
                    </td>

                    <td>
                      <div className='text-sm mb-2 text-gray-500'>
                      {new Date(item.date_created).toLocaleDateString()}
                      </div>

                      <div className='font-bold'>
                       {item.registered_user_id}: {item.first_name} {item.last_name}
                      </div>

                      <div className='mb-3 text-gray-500'>
                        {item.job_title}
                      </div>

                      <div className='text-sm text-gray-700'>
                        <i>{item.email}</i>
                      </div>

                      <div>
                        {item.company_id} - {item.company_name}
                      </div>
                      
                      <div className='text-sm text-blue-600'>
                        <a href={item.website} target="_blank" rel="noopener noreferrer">
                          {item.website}
                        </a>
                      </div>

                      <div className='text-sm text-gray-500'>
                        Timezone: {item.timezone || 'Not set'}
                      </div>

                      <div>
                        <strong>Source:</strong> {item.source || 'N/A'}
                      </div>
                      </td>

                    <td>{item.demo_applications}</td>
                    <td>{item.demo_approved}</td>
                    <td>{item.demo_completed}</td>
                    <td>{item.demo_declined}</td>

                    <td>
                      {item.last_demo_application ? new Date(item.last_demo_application).toLocaleDateString() : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default ActiveBuyers;